import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, ArrowLeft, FileText, Image as ImageIcon, Mic, Mail } from "lucide-react";
import SeoHead from "../components/SeoHead";

const SWIPE_COPY = {
  social: [
    {
      label: "Instagram / TikTok caption (general)",
      text: `I joined Cauldron — a sacred marketplace for serious spiritual practitioners. Tarot, Reiki, Ifá, Hoodoo, astrology, mediums — all in one verified, ID-checked community.

If you're a seeker or a practitioner, this is the platform we've been waiting for. Use my link: {LINK}

#cauldronapp #spiritualcommunity #tarot #reiki #ifa #hoodoo #astrology`
    },
    {
      label: "Twitter / X (240 chars)",
      text: `Real spiritual practitioners. ID-verified lineage. Recurring rewards in $CAUL. Cauldron is the platform we needed. Join through me: {LINK}`,
    },
    {
      label: "Facebook (longer-form)",
      text: `Friends — I'm part of something I really believe in. Cauldron App is building the first spiritual marketplace where every practitioner is verified — by lineage, by ID, by community.

Tarot readers, Reiki masters, Iyánífás, Babaláwos, Houngans, Mambos, astrologers, mediums, rootworkers — all in one place, with built-in payment, video sessions, and a rewards token.

Whether you're a seeker looking for someone real, or a practitioner ready to grow your practice — join through my link: {LINK}`,
    },
  ],
  email: [
    {
      label: "Personal email to your circle",
      text: `Hey {NAME},

You know I've been working with spirit for a long time. I just joined Cauldron — a new platform built specifically for verified spiritual practitioners. They check lineage, identity, everything. The community is real.

If you've ever wanted readings, energy work, or to apprentice with someone serious — start here: {LINK}

If you're a practitioner yourself, this is where you should be. They handle the payments, the video, even pay rewards in their own token ($CAUL).

Let me know what you think.

— {YOUR NAME}`,
    },
  ],
  dm: [
    {
      label: "Text / DM (short)",
      text: `Hey — joining Cauldron through my link helps support real spiritual practitioners: {LINK}`,
    },
    {
      label: "Cold DM to a practitioner",
      text: `Hi {NAME} — I follow your work and think you'd be a perfect fit for Cauldron, a new platform for verified spiritual practitioners ($49.99/mo). Would love to bring you in through my link if you're interested: {LINK}`,
    },
  ],
};

function CopyBlock({ text }) {
  const onCopy = () => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };
  return (
    <div className="relative">
      <pre className="whitespace-pre-wrap font-sans text-sm bg-input border border-secondary/15 p-4 rounded-sm leading-relaxed text-foreground/85">
        {text}
      </pre>
      <button
        onClick={onCopy}
        className="absolute top-3 right-3 bg-secondary/10 hover:bg-secondary/25 text-secondary p-2 rounded-sm border border-secondary/30 transition"
        data-testid="kit-copy-btn"
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function AmbassadorKit() {
  const [tab, setTab] = useState("social");
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="kit-page">
      <SeoHead
        title="Marketing Kit — Cauldron Ambassador Program"
        description="Ready-made graphics, video clips, and swipe copy for Cauldron Ambassadors."
      />
      <div className="max-w-5xl mx-auto px-6 py-16">
        <Link to="/ambassadors" className="inline-flex items-center gap-2 text-foreground/65 hover:text-secondary text-xs uppercase tracking-wider mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to program
        </Link>
        <div className="label-mystic text-secondary mb-3">Marketing Kit</div>
        <h1 className="font-serif text-4xl sm:text-5xl">Plug in your link. Post.</h1>
        <p className="text-foreground/70 mt-4 max-w-2xl leading-relaxed">
          Every block of copy below has <code className="text-secondary">{`{LINK}`}</code> placeholders.
          Replace with your personal Cauldron referral URL — find it on your{" "}
          <Link to="/ambassador/dashboard" className="text-secondary hover:underline">Ambassador Dashboard</Link>.
        </p>

        <div className="grid sm:grid-cols-3 gap-3 mt-10">
          <KitTile icon={FileText} title="Swipe Copy" active />
          <KitTile icon={ImageIcon} title="Graphics" hint="Coming soon" />
          <KitTile icon={Mic} title="Video clips" hint="Coming soon" />
        </div>

        <div className="mt-12 flex gap-2 border-b border-secondary/15 pb-px">
          {[
            ["social", "Social"],
            ["email", "Email"],
            ["dm", "DM / Text"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`px-4 py-2.5 text-xs uppercase tracking-[0.18em] transition ${
                tab === k ? "text-secondary border-b-2 border-secondary -mb-px" : "text-foreground/55 hover:text-foreground/85"
              }`}
              data-testid={`kit-tab-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-8" data-testid={`kit-section-${tab}`}>
          {SWIPE_COPY[tab].map((block, i) => (
            <div key={i}>
              <div className="label-mystic text-foreground/65 mb-3">{block.label}</div>
              <CopyBlock text={block.text} />
            </div>
          ))}
        </div>

        <div className="mt-20 parchment p-6 bg-secondary/[0.04] border-secondary/30">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-secondary mt-1 flex-shrink-0" />
            <div>
              <div className="font-serif text-xl mb-2">Need custom creative?</div>
              <p className="text-sm text-foreground/75 leading-relaxed">
                Elite and Regional Ambassadors get access to our designer for one-off
                graphics, branded video clips, and event decks. Open a request from your dashboard
                or reach out to <a href="mailto:ambassadors@thecauldron.app" className="text-secondary hover:underline">ambassadors@thecauldron.app</a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KitTile({ icon: Icon, title, hint, active }) {
  return (
    <div className={`parchment p-5 ${active ? "border-secondary/55" : "opacity-65"}`}>
      <Icon className="w-5 h-5 text-secondary" />
      <div className="font-serif text-lg mt-3">{title}</div>
      {hint && <div className="text-[11px] text-foreground/55 mt-1">{hint}</div>}
    </div>
  );
}
